<template>
    <van-popup
        v-model:show="visible"
        round
        style="width: 85%; background: transparent;"
        @click-overlay="close"
    >
        <div class="download-pop">
            <h4 class="download-title">{{ $t('downloadPlatform.title') }}</h4>

            <van-radio-group v-model="selectedMethod" class="method-options">
                <div
                    class="method-card"
                    :class="{
                        selected: selectedMethod === 'pwa' && pwaAvailable,
                        'method-card--disabled': !pwaAvailable
                    }"
                    @click="selectPwa"
                >
                    <van-radio
                        name="pwa"
                        checked-color="#01875f"
                        class="method-radio"
                        :disabled="!pwaAvailable"
                    />
                    <img
                        :src="pwaImage"
                        alt="PWA"
                        class="method-image"
                        :class="{ 'method-image--muted': !pwaAvailable }"
                    />
                    <span class="method-label">
                        {{ pwaAvailable ? $t('downloadPlatform.pwa') : $t('downloadPlatform.pwaNotSupported') }}
                    </span>
                </div>
                <div
                    class="method-card"
                    :class="{ selected: selectedMethod === 'apk' }"
                    @click="selectedMethod = 'apk'"
                >
                    <van-radio name="apk" checked-color="#01875f" class="method-radio" />
                    <img :src="apkImage" alt="APK" class="method-image" />
                    <span class="method-label">{{ $t('downloadPlatform.apk') }}</span>
                </div>
            </van-radio-group>

            <div class="action-row">
                <van-button class="confirm-btn" block @click="onConfirm">
                    {{ $t('common.ok') }}
                </van-button>
            </div>
        </div>
    </van-popup>
</template>

<script setup lang="ts">
import { ref, defineEmits, defineExpose } from 'vue';
import pwaImage from '@/assets/download/pwa-option.png';
import apkImage from '@/assets/download/apk-option.png';

const emit = defineEmits<{
    select: ['pwa' | 'apk'];
}>();

const visible = ref(false);
const pwaAvailable = ref(false);
const selectedMethod = ref<'pwa' | 'apk'>('pwa');

const open = (pwaSupported: boolean) => {
    pwaAvailable.value = pwaSupported;
    selectedMethod.value = pwaSupported ? 'pwa' : 'apk';
    visible.value = true;
};

const close = () => {
    visible.value = false;
};

const selectPwa = () => {
    if (pwaAvailable.value) {
        selectedMethod.value = 'pwa';
    }
};

const onConfirm = () => {
    const method = selectedMethod.value === 'pwa' && !pwaAvailable.value
        ? 'apk'
        : selectedMethod.value;
    visible.value = false;
    emit('select', method);
};

defineExpose({ open });
</script>

<style scoped>
.download-pop {
    background: #fff;
    color: #202124;
    padding: 12px 10px;
    border-radius: 15px;
    border: 1px solid #dadce0;
    overflow: hidden;
    box-sizing: border-box;
}

.download-title {
    text-align: center;
    margin: 4px 0 12px;
    font-size: 14px;
    font-weight: 600;
    color: #202124;
    line-height: 1.3;
}

.method-options {
    display: flex;
    flex-direction: row;
    gap: 8px;
    padding: 0 4px;
    width: 100%;
    box-sizing: border-box;
}

.method-card {
    flex: 1;
    min-width: 0;
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;
    padding: 6px 4px 8px;
    border: 2px solid transparent;
    border-radius: 10px;
    background: #f8f9fa;
    cursor: pointer;
    transition: border-color 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;
    box-sizing: border-box;
}

.method-card.selected {
    border-color: #01875f;
    box-shadow: 0 0 0 1px #01875f;
    background: #e6f3ef;
}

.method-card--disabled {
    cursor: not-allowed;
    opacity: 0.7;
}

.method-card--disabled .method-label {
    color: #5f6368;
    font-size: 10px;
    font-weight: 500;
    line-height: 1.2;
}

.method-image {
    width: 100%;
    max-width: 72px;
    height: 58px;
    object-fit: contain;
    display: block;
    pointer-events: none;
}

.method-image--muted {
    opacity: 0.45;
}

.method-label {
    margin-top: 4px;
    font-size: 11px;
    font-weight: 600;
    color: #202124;
    text-align: center;
    pointer-events: none;
    line-height: 1.2;
    width: 100%;
    word-break: break-word;
}

.method-radio {
    position: absolute;
    top: 4px;
    right: 4px;
    margin: 0;
    transform: scale(0.85);
    transform-origin: top right;
}

.action-row {
    padding: 12px 4px 4px;
}

.confirm-btn {
    border-radius: 10px;
    height: 38px;
    font-size: 13px;
    font-weight: 600;
    border: 1px solid #01875f;
    background: #01875f;
    color: #fff;
}
</style>
